import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import useUser from '../global/useUser';
import { getGiftCards } from '../../services/gift-cards';
import { SortValues } from '../../types/ui';
import { SortableKeys } from '../../types/gift-cards';

function useGetGiftCards({
  sort,
  enabled: passedEnabled = true,
}: { enabled?: boolean; sort?: SortValues<SortableKeys> } = {}) {
  const { user } = useUser();

  const enabled = !!user?.business?.id && passedEnabled;

  const queryKey = useMemo(
    () => [
      'gift-cards',
      {
        businessId: user?.business?.id,
        sort,
      },
    ],
    [user?.business?.id, sort],
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: async ({ pageParam: cursor }) =>
      getGiftCards({
        businessId: user!.business!.id!,
        sort,
        cursor,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => {
      if (!lastPage.meta?.hasMore || !lastPage.meta?.cursor) return undefined;
      return lastPage.meta.cursor;
    },
    enabled,
  });

  const giftCards = useMemo(
    () => data?.pages?.flatMap(page => page.result) ?? [],
    [data?.pages],
  );

  const totalCount = useMemo(
    () => (data?.pages ? data.pages[data.pages.length - 1].meta.count : 0),
    [data?.pages],
  );

  return {
    queryKey,
    data,
    giftCards,
    totalCount,
    isLoading: enabled && isLoading,
    isFetching,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
  };
}

export default useGetGiftCards;
